/*eslint-disable*/
import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import dayjs from "dayjs";


// components
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  Button,
} from "@mui/material";
import Breadcrumbs from '../../components/Breadcrumbs'
import StatusBadge from './components/statusBadge/StatusBadge';
import { fetchUser } from '../../services/user';
import Error404 from '../../components/error404/Error404';

// constants
import {
  getBulkStatuses,
  resetBulkStatuses,
} from '../../store/reducers/uploadStatusSlice'
import { selectBulkStatusesArray } from '../../store/selectors/uploadStatus';

// hooks
import useSort from '../../hooks/useSort';

//styles
import styles from './ViewBulkJob.module.scss'
const submitStyle = {
  backgroundColor: '#eb1700',
  marginTop: '24px',
  marginBottom: '34px',
  marginLeft: '0px',
  padding: '8px 16px',
  '@media (max-width:865px)': {
    marginTop: '0px',
    marginLeft: '12px !important',
  },
  '&:hover': {
    backgroundColor: '#CC1400',
  },
  '&:disabled': {
    backgroundColor: '#665A47',
  },
  '&:focus': {
    backgroundColor: '#CC1400',
  },
}

const viewEditStyle = {
  backgroundColor: 'rgba(98, 81, 55, 0.1)',
  marginBottom: '34px',
  marginTop: '24px',
  marginLeft: '30px !important',
  '@media (max-width:865px)': {
    marginTop: '0px',
    marginLeft: '12px !important',
    display: 'block'
  },
  padding:"8px 16px",
  color: '#000',
  '&:hover': {
    backgroundColor: 'rgba(102,90,71, 0.20)',
    color: '#000000',
  },
  '&:disabled': {
    backgroundColor: 'rgba(138, 115, 92, 0.05)',
  },
  '&:focus': {
    backgroundColor: 'rgba(112, 99, 79, 0.1)',
  },
}

function ViewBulkJob() {
  const { jobId } = useParams()
  const [isFetching, setIsFetching] = useState(false)
  const [email, setEmail] = useState('')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const bulkUserUploadStatuses = useSelector(selectBulkStatusesArray);
  const intervalRef = useRef(null);

  const breadcrumbPath = [
    {label: 'audit-bulk-status-tab', path: '/bulk-users-activity'},
    {label: 'view-bulk-job-tab'}
  ]

  const { t } = useTranslation();

  const getJobStatus = async () => {
    setIsFetching(true)
    try {
      if (jobId) {
        await dispatch(getBulkStatuses({ fileName: jobId })).unwrap()
      }
    } catch (error) {
      dispatch(resetBulkStatuses())
      if(error?.message === "Job not found."){
        cleanUpInterval()
      }
      toast.error(`${error?.message ? error.message : t('no-records-found')}`)
    } finally {
      setIsFetching(false)
    }
  }

  const checkJobStatus = async () => {
    try {
      if (jobId) {
        await dispatch(getBulkStatuses({ fileName: jobId })).unwrap()
      }
    } catch (error) {
      dispatch(resetBulkStatuses())
      toast.error(`${error?.message ? error.message : t('no-status-found')}`)
    }
  }

  const cleanUpInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    return 
  }


  useEffect(() => {
    getJobStatus()
  }, [])

  useEffect(() => {
    cleanUpInterval();

    const status = bulkUserUploadStatuses.currentStatus

    if (status && status === "processed" || status === "completed" || status === "failed" || status === "failed-validation") {
      return 
    }

    intervalRef.current = setInterval(() => {
      checkJobStatus()
    }, 30000
    )

    return () => {
      cleanUpInterval()
    }

  }, [bulkUserUploadStatuses.currentStatus])

  useEffect(() => {
    if (bulkUserUploadStatuses.jobOwner) {
      const fetchEmail = async () => {
        try {
          const userData = await fetchUser(bulkUserUploadStatuses.jobOwner);
          setEmail(userData?.data?.userName);
        } catch (error) {
          console.error(error);
        }
      };

      fetchEmail();
    }
  }, [bulkUserUploadStatuses.jobOwner]);



  const { sortBy, sortOrder, handleSort } = useSort({
    initialSortBy: "",
    initialSortOrder: "asc",
  });


  const loaderCircle = () => (
      <div className={styles.loaderContainer}>
        <div className={styles.loading}>Loading...</div>
        <CircularProgress
          sx={{
            color: "black",
          }}
        />
      </div>
    )

  const renderTableHead = () => (
    <TableRow>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === "processedTimeStamp"}
          direction={sortOrder}
          onClick={() => handleSort("processedTimeStamp")}
        >
          {t('jobStatus.dateCreated')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === "email"}
          direction={sortOrder}
          onClick={() => handleSort("email")}
        >
          {t('jobStatus.fileOwner')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === "fileName"}
          direction={sortOrder}
          onClick={() => handleSort("fileName")}
        >
          {t('jobStatus.jobID')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === "status"}
          direction={sortOrder}
          onClick={() => handleSort("status")}
        >
          {t('jobStatus.status')}
        </TableSortLabel>
      </TableCell>
    </TableRow>
  );

  const renderTableBody = () => {
    // return bulkUserUploadStatuses.map((bulkUploadStatus) => (
    return (
      <TableRow className={styles.tableBodyRow} key={bulkUserUploadStatuses.id}>
        <TableCell 
          className={styles.tableCell}
          data-label = {t('jobStatus.dateCreated')}
        >
          <span className={styles.cellValue}>
            {dayjs(bulkUserUploadStatuses?.creationTime).format('MM/DD/YYYY hh:mm:ssA')}
          </span>
          
        </TableCell>

        <TableCell 
          className={styles.tableCell}
          data-label={t('jobStatus.fileOwner')}
        >
          <span className={styles.cellValue}>{email}</span>
        </TableCell>
        <TableCell 
          // sx={{ width: "250px" }}
          className={styles.tableCell}
          data-label={t('jobStatus.jobID')}
        >
          <span className={styles.cellValue}>{bulkUserUploadStatuses?.id}</span>
        </TableCell>
        <TableCell 
          className={styles.tableCell}
          data-label={t('jobStatus.status')}
        >
          <span className={styles.cellValue}><StatusBadge status={bulkUserUploadStatuses?.currentStatus} /></span>
        </TableCell>
      </TableRow>
    );
  };

  const renderLoaderInTableBody = () => {
    return (
      <TableRow>
        <TableCell sx={{ border: "none" }} colSpan={6}>
          {loaderCircle()}
        </TableCell>
      </TableRow>
    );
  };

  const renderTable = () => {
    return (
      <>
        <div>
          <p className={styles.results}>
            <strong>{t('jobStatus.recentUpload')}</strong>
            <span>
            {t('jobStatus.realTimeData')}
            </span>
          </p>
          <TableContainer className={styles.tableContainer}>
            <Table stickyHeader className={styles.userTable}>
              <TableHead>{renderTableHead()}</TableHead>
              <TableBody>
                {isFetching ? renderLoaderInTableBody() : renderTableBody()}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </>
    );
  };

  const renderViewJob = () => {
    return (
    <div className={styles.container}>
        <div className={styles.header}>
          <strong>{t('jobStatus.jobStatusPage')}: </strong>
          <span> {bulkUserUploadStatuses?.id} </span>
        </div>
        {renderTable()}
        <div className={styles.buttonContainer}>
          <Button
            onClick={()=> navigate('/bulk-users-activity')}
            className='primary-btn'
            disableElevation
            sx={ submitStyle
            }
            variant="contained">
            {t('jobStatus.auditButton')}
          </Button>
          <Button
            onClick={()=> navigate('/create-user', {state: {selectedTabIndex: 1}})}
            className='secondary-btn'
            disableElevation
            sx={ viewEditStyle
            }
            variant="contained">
            {t('jobStatus.bulkButton')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      {(!isFetching && Object.keys(bulkUserUploadStatuses).length === 0) && (
        <Error404 />
      )}
      {(!isFetching && Object.keys(bulkUserUploadStatuses).length > 0) && (
        <div style={{ height: '100vh' }}>
          <div className={styles.stickyHeader} style={{
            backgroundColor: '#F5F5F5',
            width: '100vw',
            padding: '20px',
          }}>
            <Breadcrumbs className={styles.stickyHeader} crumbs={breadcrumbPath} />
          </div>
          {renderViewJob()}
        </div>
      )}
      {(isFetching && 
        <div style={{
            width: '100vw',
            paddingTop: '121px'
          }}>
          {loaderCircle()}
        </div>
      )}
    </>
  )
}

export default ViewBulkJob
